import { findEvent } from '../../../packages/src/api/index.js';
import localization from '../localization/page.js';
import { occoApi } from './occoAPI-module.js';
import { showBlockingError } from './utils-module.js';

const MAX_NUMBER_OF_PHOTOS = 3000;

export const pageParams = {
  resetData: () => localStorage.clear(),
  eventName: () => localStorage.getItem('eventName'),
  saveEventName: (eventName) => localStorage.setItem('eventName', eventName),
  homeLink: () => localStorage.getItem('locWA'),
  saveHomeLink: (homeLink) => localStorage.setItem('locWA', homeLink),
  attendeeUid: () => localStorage.getItem('attUidWA'),
  saveAttendeeUid: (attendeeUid) => localStorage.setItem('attUidWA', attendeeUid),
  generatedAttId: () => localStorage.getItem('generatedAttId'),
  saveGeneratedAttId: (generatedAttId) => localStorage.setItem('generatedAttId', generatedAttId),
};

/**
 * @typedef {Object} ValidationVariant
 * @property {Function} isValid - Function that returns true if validation passes
 * @property {String} message - Error message to display if validation fails
 */

/**
 * Make a validation variant
 *
 * @param isValid {Function}
 * @param message {String}
 * @returns {ValidationVariant}
 */
function makeValidationVariant(isValid, message) {
  return {
    isValid,
    message,
  };
}

export const validationVariants = {
  isAssignedAttendeeUid: makeValidationVariant(
    () => pageParams.attendeeUid() !== null,
    "We couldn't find your account information."
  ),
  isSetUpEventName: makeValidationVariant(
    () => Boolean(pageParams.eventName()),
    'The event code is missing or invalid.Please contact support for assistance.'
  ),
};

/**
 * Performs validation check on the provided validation list
 * @private
 * @param {ValidationVariant[]} validationList - Array of validation variants to check
 * @returns {ValidationVariant|null} The first failed validation, or null if all pass
 */
function performValidation(validationList) {
  if (!Array.isArray(validationList) || validationList.length === 0) {
    return { message: 'Invalid validation list provided' };
  }

  return (
    validationList.find((validation) => {
      if (!validation || typeof validation.isValid !== 'function') {
        return true;
      }
      return !validation.isValid();
    }) || null
  );
}

/**
 * Handles validation error by displaying blocking error message
 * @private
 * @param {string} message - Error message to display
 */
function handleValidationError(message) {
  showBlockingError(message || 'Validation failed');
}

/**
 * Validates page parameters and shows blocking error if validation fails
 * Returns validation result - callers decide how to handle failure
 *
 * @param {ValidationVariant[]} validationList - Array of validation variants to check
 * @returns {boolean} True if validation passed, false if validation failed
 */
export function validatePageParams(validationList) {
  const failedValidation = performValidation(validationList);

  if (failedValidation) {
    handleValidationError(failedValidation.message);
    return false;
  }

  return true;
}

export const fetchData = {
  event: async (eventName = pageParams.eventName()) => {
    const { result } = await findEvent({ eventName });
    if (!result) {
      showBlockingError(localization.eventNotExist(eventName));
      return null;
    }
    return result;
  },
  photos: async (eventId, attUid = pageParams.attendeeUid()) =>
    new Promise((resolve, reject) => {
      occoApi.photoListRetrieve(
        'attendee_uid',
        attUid,
        eventId,
        null,
        null,
        null,
        MAX_NUMBER_OF_PHOTOS,
        null,
        null,
        null,
        false,
        null,
        null,
        null,
        null,
        null,
        attUid,
        true,
        null,
        null,
        (err, result) => {
          if (err) {
            reject(err);
          } else {
            resolve(result);
          }
        }
      );
    }),
};
